import React, { useEffect, useState, useMemo } from 'react';
import { BookOpen, Info, TrendingUp, Target, Lightbulb, ChevronDown, ChevronUp, ChevronRight, ExternalLink, Calculator, FlaskConical, ArrowRight, Activity, Layers, CheckCircle2, Users, Briefcase, Gauge, DollarSign, Search, X, Download, FileText, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getLEIMeasures } from '../lib/api';
import api from '../lib/api';
import logger from '../lib/logger';
import RelatedReferences from '../components/reference/RelatedReferences';
import WeakLinkCallout from '../components/lei/WeakLinkCallout';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

// Learning Effectiveness Value Chain — each stage maps 1:1 to a pillar id
// returned by /api/lei-measures so we can surface the pillar's measures
// in a tooltip without another round trip.
//
// `healthId` is the matching pillar key returned by /lei-health/overview
// (P1..P5) — used to overlay the org's actual pillar score on the
// chain so the diagram reads as "this is where *you* stand".
const VALUE_CHAIN_STAGES = [
  {
    id: 'access',
    label: 'Learning Access',
    pillarId: 'P1 Access & Engagement',
    pillarLabel: 'Access & Engagement measures',
    healthId: 'P1',
    classes: 'bg-blue-100 text-blue-700 border-blue-400/40',
  },
  {
    id: 'outcomes',
    label: 'Learning Outcomes',
    pillarId: 'P2 Performance & Outcomes',
    pillarLabel: 'Performance & Outcomes measures',
    healthId: 'P2',
    classes: 'bg-emerald-100 text-emerald-700 border-emerald-400/40',
  },
  {
    id: 'capability',
    label: 'Workforce Capability',
    pillarId: 'P3 Capability & Readiness',
    pillarLabel: 'Capability & Readiness measures',
    healthId: 'P3',
    classes: 'bg-purple-100 text-purple-700 border-purple-400/40',
  },
  {
    id: 'performance',
    label: 'Business Performance',
    pillarId: 'P4 Operational Efficiency',
    pillarLabel: 'Operational Efficiency measures',
    healthId: 'P4',
    classes: 'bg-amber-100 text-amber-700 border-amber-400/40',
  },
  {
    id: 'roi',
    label: 'Financial ROI',
    pillarId: 'P5 Business Impact Alignment',
    pillarLabel: 'Business Impact Alignment measures',
    healthId: 'P5',
    classes: 'bg-teal-100 text-teal-700 border-teal-400/40',
  },
];

// PDF Download URL
const LEI_PDF_URL = 'https://customer-assets.emergentagent.com/job_788c909c-5934-4100-969a-b26c70563a55/artifacts/fedws3tz_l_d-strategy---how-to-measure-the-impact-of-learning-effectiveness_v3.pdf';

// Pillar colors for visual grouping
const PILLAR_COLORS = {
  'P1 Access & Engagement': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', icon: Users },
  'P2 Performance & Outcomes': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', icon: Target },
  'P3 Capability & Readiness': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', icon: Briefcase },
  'P4 Operational Efficiency': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: Gauge },
  'P5 Business Impact Alignment': { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700', icon: DollarSign },
};

// Pillar descriptions
const PILLAR_DESCRIPTIONS = {
  'P1 Access & Engagement': {
    weight: '20%',
    summary: 'Measures the breadth and inclusiveness of learning opportunities across the organization.',
    focus: 'Participation rates, reach, and voluntary engagement — indicators of how accessible, equitable, and relevant your learning ecosystem truly is.',
    whyItMatters: 'You can\'t improve what people don\'t experience. High engagement reflects a culture of learning and ensures learning is reaching the right people, in the right ways, at the right time.',
    businessImpact: ['Employee engagement scores', 'Learning culture strength', 'Equity of development access', 'Platform utilization ROI']
  },
  'P2 Performance & Outcomes': {
    weight: '30%',
    summary: 'Evaluates how well programs achieve intended knowledge and behavior outcomes.',
    focus: 'Assessments, skill uplift, NPS scores, and Kirkpatrick Levels 1–3 — the "effectiveness" in Learning Effectiveness.',
    whyItMatters: 'This captures how well learners absorb and apply new knowledge. Without measurable outcomes, learning is just activity, not impact.',
    businessImpact: ['Competency development velocity', 'Training effectiveness', 'Learner satisfaction', 'Knowledge retention']
  },
  'P3 Capability & Readiness': {
    weight: '20%',
    summary: 'Focuses on whether learning is preparing the workforce for current and future roles.',
    focus: 'Skills gap closure, bench strength, certification progression, and succession readiness.',
    whyItMatters: 'This is where learning meets workforce strategy — linking L&D to succession planning, talent mobility, and organizational resilience.',
    businessImpact: ['Succession pipeline strength', 'Skills gap reduction', 'Internal mobility rate', 'Time to productivity']
  },
  'P4 Operational Efficiency': {
    weight: '20%',
    summary: 'Measures cost, time, and resource effort required to develop, deliver, and manage learning.',
    focus: 'Supports capacity planning, technology ROI, and demonstrates stewardship over resources and scalability.',
    whyItMatters: 'Learning must prove not only impact, but efficiency. This shows you\'re maximizing value from every dollar invested.',
    businessImpact: ['Cost per learner', 'Development efficiency', 'Technology utilization', 'Resource optimization']
  },
  'P5 Business Impact Alignment': {
    weight: '10%',
    summary: 'Connects learning to downstream business outcomes — productivity, performance, retention, and cost-effectiveness.',
    focus: 'The "holy grail" for L&D — proving learning investments drive real business results.',
    whyItMatters: 'This unlocks strategic alignment and earns L&D a seat at the executive table by speaking the language of business results.',
    businessImpact: ['Revenue contribution', 'Productivity improvement', 'Retention impact', 'Strategic goal alignment']
  }
};

const MeasureCard = ({ measure, isExpanded, onToggle }) => {
  const pillarStyle = PILLAR_COLORS[measure.pillar] || PILLAR_COLORS['P1 Access & Engagement'];
  
  return (
    <Card className={`${pillarStyle.border} border-l-4 transition-all duration-200 hover:shadow-md`} id={measure.id}>
      <CardHeader className="pb-2 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full ${pillarStyle.badge}`}>
                {measure.pillar}
              </span>
              <span className="text-xs text-slate-500">
                Elasticity: {measure.elasticity_moderate}
              </span>
            </div>
            <CardTitle className="text-lg">{measure.name}</CardTitle>
            <CardDescription className="mt-1">{measure.description}</CardDescription>
          </div>
          <Button variant="ghost" size="sm" className="flex-shrink-0">
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-0">
          <div className="grid gap-4 mt-4">
            {/* What it measures */}
            <div className={`p-3 rounded-lg ${pillarStyle.bg}`}>
              <div className="flex items-center gap-2 mb-2">
                <Target className={`h-4 w-4 ${pillarStyle.text}`} />
                <h4 className={`font-semibold text-sm ${pillarStyle.text}`}>What It Measures</h4>
              </div>
              <p className="text-sm text-slate-700">{measure.what_it_measures}</p>
            </div>
            
            {/* LEI Formula - How to calculate this measure */}
            {measure.lei_formula && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="h-4 w-4 text-blue-700" />
                  <h4 className="font-semibold text-sm text-blue-700">How to Calculate This LEI Measure</h4>
                </div>
                <div className="bg-white p-3 rounded border font-mono text-sm text-blue-800 mb-3">
                  {measure.lei_formula.formula}
                </div>
                <div className="mb-3">
                  <p className="text-xs font-medium text-blue-700 mb-2">Input Variables:</p>
                  <div className="space-y-2">
                    {measure.lei_formula.variables?.map((variable, idx) => (
                      <div key={variable.name || `var-${idx}`} className="bg-white border border-slate-200 p-2 rounded text-sm">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-blue-800 min-w-[140px]">{variable.name}</span>
                          <span className="text-slate-600">{variable.description}</span>
                        </div>
                        {variable.formula && (
                          <p className="text-xs font-mono text-blue-600 mt-1 pl-[148px]">{variable.formula}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-100/50 p-2 rounded">
                  <p className="text-xs font-medium text-blue-700 mb-1">Example:</p>
                  <p className="text-sm text-blue-800">{measure.lei_formula.example}</p>
                </div>
              </div>
            )}
            
            {/* Arrow indicating flow from LEI to KPI */}
            {measure.lei_formula && measure.calculation_logic && (
              <div className="flex justify-center items-center py-2">
                <div className="flex items-center gap-2 text-slate-500">
                  <span className="text-xs">LEI Value</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="text-xs">Improvement %</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="text-xs">KPI Impact</span>
                </div>
              </div>
            )}
            
            {/* Calculation Logic - How this impacts KPIs */}
            {measure.calculation_logic && (
              <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="h-4 w-4 text-teal-700" />
                  <h4 className="font-semibold text-sm text-teal-700">How This LEI Impacts KPIs</h4>
                </div>
                <div className="bg-white p-3 rounded border font-mono text-sm text-slate-800 mb-3">
                  {measure.calculation_logic.formula}
                </div>
                <div className="mb-3">
                  <p className="text-xs font-medium text-slate-600 mb-1">Example Calculation:</p>
                  <p className="text-sm text-slate-700 bg-white/50 p-2 rounded">{measure.calculation_logic.example}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-600">Formula Components:</p>
                  {measure.calculation_logic.components?.map((comp, idx) => (
                    <div key={comp.name || `comp-${idx}`} className="flex gap-2 text-sm bg-white/50 p-2 rounded">
                      <span className="font-semibold text-slate-700 min-w-[120px]">{comp.name}</span>
                      <span className="text-slate-600">{comp.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Improvement Guidance */}
            <div className="p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-teal-600" />
                <h4 className="font-semibold text-sm text-teal-700">Improvement Guidance</h4>
              </div>
              <p className="text-sm text-slate-700">{measure.improvement_guidance}</p>
            </div>
            
            {/* Typical Range */}
            <div className="p-3 rounded-lg bg-amber-50">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-amber-600" />
                <h4 className="font-semibold text-sm text-amber-700">Typical Improvement Range</h4>
              </div>
              <p className="text-sm text-slate-700">{measure.typical_range}</p>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const LEIReferencePage = () => {
  const [measures, setMeasures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMeasures, setExpandedMeasures] = useState({});
  const [selectedPillar, setSelectedPillar] = useState('all');
  const [leiExplainerExpanded, setLeiExplainerExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Org's actual pillar scores from /lei-health/overview — used to
  // personalize the value chain ("here's where *you* stand").
  // null when the request hasn't resolved or RBAC blocks the read,
  // which keeps the chain functional as a generic teaching aid.
  const [orgPillars, setOrgPillars] = useState(null);
  const [orgOverall, setOrgOverall] = useState(null);

  // Filter measures based on search query and selected pillar
  const filteredMeasures = useMemo(() => {
    let filtered = measures;
    
    // Filter by pillar
    if (selectedPillar !== 'all') {
      filtered = filtered.filter(m => m.pillar === selectedPillar);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.name.toLowerCase().includes(query) ||
        m.description.toLowerCase().includes(query) ||
        (m.what_it_measures && m.what_it_measures.toLowerCase().includes(query)) ||
        (m.why_it_matters && m.why_it_matters.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [measures, selectedPillar, searchQuery]);

  // Group filtered measures by pillar for display
  const groupedMeasures = useMemo(() => {
    const groups = {};
    filteredMeasures.forEach(measure => {
      if (!groups[measure.pillar]) {
        groups[measure.pillar] = [];
      }
      groups[measure.pillar].push(measure);
    });
    return groups;
  }, [filteredMeasures]);

  // Track expanded pillars (all collapsed by default)
  const [expandedPillars, setExpandedPillars] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await getLEIMeasures();
        setMeasures(response.data);
        // Individual measures collapsed by default
        const expanded = {};
        response.data.forEach(m => { expanded[m.id] = false; });
        setExpandedMeasures(expanded);
        // Pillars collapsed by default
        setExpandedPillars({});
      } catch (error) {
        logger.error('Error loading measures:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Pull the org's live LEI pillar scores in parallel — failure here
  // is non-fatal and just leaves the page in its generic mode.
  useEffect(() => {
    let cancelled = false;
    api.get('/lei-health/overview')
      .then((res) => {
        if (cancelled) return;
        if (Array.isArray(res?.data?.pillars)) {
          setOrgPillars(res.data.pillars);
        }
        if (typeof res?.data?.overall_score === 'number') {
          setOrgOverall(res.data.overall_score);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Index pillar scores by P1..P5 so the value-chain renderer is O(1).
  const orgPillarById = useMemo(() => {
    if (!orgPillars) return {};
    return orgPillars.reduce((acc, p) => {
      if (p?.id) acc[p.id] = p;
      return acc;
    }, {});
  }, [orgPillars]);

  // Lowest-scoring pillar = primary constraint = the user's "weak link"
  // in the value chain. Tie-break by encounter order so the result
  // is deterministic across renders.
  const weakestPillarId = useMemo(() => {
    if (!orgPillars || orgPillars.length === 0) return null;
    return orgPillars.reduce(
      (lo, p) => (lo == null || p.score < lo.score ? p : lo),
      null,
    )?.id ?? null;
  }, [orgPillars]);

  const weakestPillar = weakestPillarId ? orgPillarById[weakestPillarId] : null;
  const weakestStage = weakestPillarId
    ? VALUE_CHAIN_STAGES.find((s) => s.healthId === weakestPillarId)
    : null;

  const toggleMeasure = (measureId) => {
    setExpandedMeasures(prev => ({
      ...prev,
      [measureId]: !prev[measureId]
    }));
  };

  const togglePillar = (pillar) => {
    setExpandedPillars(prev => ({
      ...prev,
      [pillar]: !prev[pillar]
    }));
  };

  const toggleAllPillars = (expand) => {
    const newState = {};
    pillars.forEach(p => { newState[p] = expand; });
    setExpandedPillars(newState);
  };

  const toggleAll = (expand) => {
    const newState = {};
    measures.forEach(m => { newState[m.id] = expand; });
    setExpandedMeasures(newState);
    // Also expand/collapse all pillars
    toggleAllPillars(expand);
  };

  // Group measures by pillar
  const pillars = [...new Set(measures.map(m => m.pillar))];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  const leiRefHeroActions = (
    <a
      href={LEI_PDF_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => api.post('/analytics/whitepaper/track', {
        whitepaper: 'lei_measures',
        source: 'header_pill',
        page_path: window.location.pathname,
      }).catch(() => {})}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${__pageTheme.actionBtn}`}
      data-testid="lei-ref-download-pdf"
    >
      <Download className="h-4 w-4" />
      Download Full Guide (PDF)
    </a>
  );
  const leiRefHeroStats = [
    { label: 'Total Measures', value: measures.length, testId: 'lei-ref-stat-measures' },
    { label: 'Pillars', value: 5, testId: 'lei-ref-stat-pillars' },
    { label: 'Expanded', value: Object.keys(expandedMeasures).filter(k => expandedMeasures[k]).length, testId: 'lei-ref-stat-expanded' },
    { label: 'Search Results', value: filteredMeasures.length, testId: 'lei-ref-stat-search' },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="LEI Measures Guide"
        description="Understanding the Learning Effectiveness Index and how to apply it. Explore all 15 measures across 5 pillars with formulas, benchmarks, and best practices."
        theme={__pageTheme}
        icon={BookOpen}
        actions={leiRefHeroActions}
        stats={leiRefHeroStats}
        testId="lei-ref-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6">

      {/* LEI Explainer Section */}
      <Card className="bg-white border border-slate-200 text-slate-900 overflow-hidden">
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setLeiExplainerExpanded(!leiExplainerExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold flex items-center gap-3">
              <Activity className="h-6 w-6 text-teal-600" />
              What is the Learning Effectiveness Index (LEI)?
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-slate-900 hover:bg-slate-50">
              {leiExplainerExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          </div>
          {!leiExplainerExpanded && (
            <p className="text-slate-600 text-sm mt-2">
              A strategic, metrics-driven framework that quantifies L&D impact across 5 pillars...
            </p>
          )}
        </CardHeader>
        
        {leiExplainerExpanded && (
          <CardContent className="space-y-6">
            {/* Introduction */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-slate-700 leading-relaxed">
                In an era where business transformation is constant and talent is the ultimate competitive advantage, 
                L&D must evolve beyond delivering programs — it must <span className="font-semibold text-teal-700">drive measurable value</span>. 
                The Learning Effectiveness Index (LEI) brings learning outcomes, operational efficiency, and business 
                relevance into one cohesive model.
              </p>
              <p className="text-slate-600 mt-3 text-sm">
                LEI empowers L&D to operate as a <span className="font-medium text-slate-900">business partner</span>, not just a service provider. 
                It enables alignment with strategic priorities, clarity in resource allocation, and credibility in leadership discussions.
              </p>
            </div>

            {/* Learning Effectiveness Value Chain */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-bold text-slate-900 text-base mb-2 text-center">The Learning Effectiveness Value Chain</h3>

              {/* Personalized "your weak link" call-out — shared
                  component used here and on the LEI Health
                  Dashboard. Renders nothing if the org has no LEI
                  pillar scores yet, so this gate is purely visual. */}
              {weakestStage && weakestPillar && (
                <div className="mb-4 mx-auto max-w-3xl">
                  <WeakLinkCallout
                    pillars={orgPillars}
                    orgOverall={orgOverall}
                    variant="light"
                    source="lei_reference_weak_link"
                    testIdPrefix="lei-ref-weak-link"
                  />
                </div>
              )}

              <p className="text-xs text-slate-500 text-center mb-4">
                {orgPillars
                  ? 'Each stage shows your live pillar score. Hover any stage to see the measures that live there.'
                  : 'Hover any stage to see the measures that live there.'}
              </p>

              <TooltipProvider delayDuration={150}>
                <div className="flex flex-wrap lg:flex-nowrap items-stretch lg:items-center justify-center gap-2 lg:gap-3">
                  {VALUE_CHAIN_STAGES.map((stage, idx) => {
                    const orgPillar = orgPillarById[stage.healthId];
                    const isWeakest = stage.healthId === weakestPillarId;
                    const score = orgPillar?.score;
                    // Score-band tint applied as a *ring* so the base
                    // pill color (which encodes pillar identity) is
                    // never overwritten — preserves the visual
                    // grammar of the chain.
                    const scoreRing =
                      score == null
                        ? ''
                        : score >= 70
                          ? 'ring-2 ring-emerald-400/60'
                          : score >= 50
                            ? 'ring-2 ring-amber-400/60'
                            : 'ring-2 ring-rose-400/70';
                    const weakRing = isWeakest
                      ? 'ring-2 ring-rose-300 ring-offset-2 ring-offset-white animate-pulse-slow'
                      : '';
                    return (
                      <React.Fragment key={stage.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={`${stage.classes} ${weakRing || scoreRing} relative px-4 py-3 rounded-lg font-semibold text-sm border flex-1 min-w-[140px] max-w-[200px] text-center cursor-help transition-transform hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40`}
                              data-testid={`value-chain-pill-${stage.id}`}
                            >
                              {isWeakest && (
                                <span
                                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-rose-500 text-slate-900 shadow-md"
                                  data-testid={`value-chain-pill-weakest-${stage.id}`}
                                >
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  Value Leak
                                </span>
                              )}
                              <span className="block">{stage.label}</span>
                              {score != null && (
                                <span
                                  className="block text-[10px] font-bold mt-1 tabular-nums opacity-90"
                                  data-testid={`value-chain-pill-score-${stage.id}`}
                                >
                                  {Math.round(score)}/100
                                </span>
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="max-w-xs bg-slate-900 text-slate-900 border border-slate-200 p-3"
                          >
                            <p className="text-[11px] font-semibold tracking-wide uppercase text-slate-600 mb-1">
                              {stage.pillarLabel}
                            </p>
                            {orgPillar && (
                              <p className="text-xs text-slate-700 mb-2">
                                <span className="text-slate-500">Your score:</span>{' '}
                                <strong className={
                                  orgPillar.score >= 70
                                    ? 'text-emerald-700'
                                    : orgPillar.score >= 50
                                      ? 'text-amber-700'
                                      : 'text-rose-700'
                                }>
                                  {Math.round(orgPillar.score)}/100
                                </strong>
                                {typeof orgPillar.trend === 'number' && (
                                  <span className="text-slate-500">
                                    {' '}· trend {orgPillar.trend > 0 ? '+' : ''}{orgPillar.trend.toFixed(1)}
                                  </span>
                                )}
                                {isWeakest && (
                                  <span className="ml-1.5 text-[10px] font-bold px-1 py-0.5 rounded bg-rose-600 text-slate-900">
                                    PRIMARY CONSTRAINT
                                  </span>
                                )}
                              </p>
                            )}
                            {(() => {
                              const stageMeasures = measures.filter((m) => m.pillar === stage.pillarId);
                              if (stageMeasures.length === 0) {
                                return <p className="text-xs text-slate-500 italic">Loading measures…</p>;
                              }
                              return (
                                <ul className="space-y-1">
                                  {stageMeasures.map((m) => (
                                    <li key={m.id} className="text-xs text-slate-100 flex items-start gap-1.5">
                                      <span className="text-teal-700 mt-0.5">•</span>
                                      <span>{m.name}</span>
                                    </li>
                                  ))}
                                </ul>
                              );
                            })()}
                          </TooltipContent>
                        </Tooltip>
                        {idx < VALUE_CHAIN_STAGES.length - 1 && (
                          <>
                            <ArrowRight className="h-5 w-5 text-slate-500 hidden lg:inline-block flex-shrink-0" />
                            <ChevronDown className="h-5 w-5 text-slate-500 lg:hidden mx-auto my-1" />
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </TooltipProvider>
              <div className="mt-5 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-600 text-center leading-relaxed">
                  Learning only drives business value when improvements in learning effectiveness 
                  translate into workforce capability and operational performance.
                </p>
                <p className="text-sm text-teal-700 font-medium text-center mt-2">
                  The LEI framework measures this entire chain.
                </p>
              </div>
            </div>

            {/* The 5 Pillars */}
            <div>
              <h3 className="font-bold text-slate-900 text-lg mb-4 flex items-center gap-2">
                <Layers className="h-5 w-5 text-teal-600" />
                The 5 Pillars of Learning Effectiveness
              </h3>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {/* Pillar 1 */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Users className="h-4 w-4 text-blue-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">Access & Engagement</h4>
                      <span className="text-xs text-blue-700">20% Weight</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600">Participation rates, reach, and voluntary engagement across the organization.</p>
                </div>

                {/* Pillar 2 */}
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Target className="h-4 w-4 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">Performance & Outcomes</h4>
                      <span className="text-xs text-emerald-700">30% Weight</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600">Knowledge and behavior outcomes, assessments, skill uplift, NPS scores.</p>
                </div>

                {/* Pillar 3 */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Briefcase className="h-4 w-4 text-purple-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">Capability & Readiness</h4>
                      <span className="text-xs text-purple-700">20% Weight</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600">Skills gap closure, bench strength, certification progression.</p>
                </div>

                {/* Pillar 4 */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Gauge className="h-4 w-4 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">Operational Efficiency</h4>
                      <span className="text-xs text-amber-700">20% Weight</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600">Cost, time, and resource efficiency in learning delivery.</p>
                </div>

                {/* Pillar 5 */}
                <div className="bg-teal-50 rounded-lg p-4 border border-teal-200 md:col-span-2 lg:col-span-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-teal-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">Business Impact</h4>
                      <span className="text-xs text-teal-700">10% Weight</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600">Connection to business outcomes — productivity, retention, ROI.</p>
                </div>
              </div>
            </div>

            {/* Value Proposition */}
            <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-teal-600" />
                Why LEI Matters for Learning Leaders
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900 text-sm">Execution Realism</span>
                    <p className="text-xs text-slate-600">Know how much of your business plan your workforce can deliver</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900 text-sm">Early Warning</span>
                    <p className="text-xs text-slate-600">See capability gaps before performance drops</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900 text-sm">ROI Proof</span>
                    <p className="text-xs text-slate-600">Every point increase corresponds to measurable business improvement</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900 text-sm">Strategic Credibility</span>
                    <p className="text-xs text-slate-600">Move from cost center to value generator with defensible data</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom quote */}
            <div className="text-center pt-2">
              <p className="text-slate-500 text-xs italic">
                "LEI is your ticket to the C-Level table as a trusted strategic advisor."
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Search and Filter Section */}
      <Card>
        <CardContent className="py-4 space-y-4">
          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              type="text"
              placeholder="Search measures... (e.g., 'skills', 'NPS', 'retention')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Search Results Count */}
          {searchQuery && (
            <p className="text-sm text-slate-600">
              Found <span className="font-semibold text-teal-600">{filteredMeasures.length}</span> measure{filteredMeasures.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          )}
          
          {/* Pillar Filter */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-600 mr-2">Filter by Pillar:</span>
            <Button
              variant={selectedPillar === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPillar('all')}
              className={selectedPillar === 'all' ? 'bg-slate-700' : ''}
            >
              All ({measures.length})
            </Button>
            {pillars.map(pillar => {
              const count = measures.filter(m => m.pillar === pillar).length;
              const style = PILLAR_COLORS[pillar];
              return (
                <Button
                  key={pillar}
                  variant={selectedPillar === pillar ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPillar(pillar)}
                  className={selectedPillar === pillar ? style?.badge : ''}
                >
                  {pillar.split(' ')[0]} ({count})
                </Button>
              );
            })}
          </div>
          <div className="flex gap-2 pt-3 border-t">
            <Button variant="outline" size="sm" onClick={() => toggleAll(true)}>
              Expand All
            </Button>
            <Button variant="outline" size="sm" onClick={() => toggleAll(false)}>
              Collapse All
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Understanding the Framework */}
      <Card className="bg-gradient-to-r from-navy-50 to-teal-50 border-navy-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Info className="h-5 w-5 text-navy-600" />
            Understanding the LEI Framework
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          <p>
            The <strong>Learning Effectiveness Index (LEI)</strong> framework measures 15 dimensions of learning 
            impact across 5 pillars. Each measure has an <strong>elasticity coefficient</strong> that determines 
            how strongly improvements translate to business KPI impact.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="p-3 bg-white/50 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-1">How to Set Improvement Targets</h4>
              <ul className="space-y-1 text-xs">
                <li>• Start with your current baseline measurements</li>
                <li>• Consider organizational maturity and constraints</li>
                <li>• Use the "Typical Range" as a guide</li>
                <li>• Higher elasticity measures have more impact per % improvement</li>
              </ul>
            </div>
            <div className="p-3 bg-white/50 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-1">Interpreting Results</h4>
              <ul className="space-y-1 text-xs">
                <li>• KPI impact = LEI improvement × weight × elasticity × dampening</li>
                <li>• Portfolio dampening accounts for overlapping effects</li>
                <li>• Higher weights indicate stronger causal relationships</li>
                <li>• Monte Carlo simulation shows probability ranges</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Measure Cards - Grouped by Pillar with Descriptions */}
      <div className="space-y-6">
        {Object.keys(groupedMeasures).length === 0 ? (
          <Card className="bg-slate-50">
            <CardContent className="py-8 text-center">
              <Search className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-600">No measures found matching your search.</p>
              <Button variant="link" onClick={() => { setSearchQuery(''); setSelectedPillar('all'); }}>
                Clear filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          pillars.filter(pillar => groupedMeasures[pillar]?.length > 0).map(pillar => {
            const pillarStyle = PILLAR_COLORS[pillar];
            const pillarDesc = PILLAR_DESCRIPTIONS[pillar];
            const IconComponent = pillarStyle?.icon || Users;
            const isPillarExpanded = expandedPillars[pillar];
            
            return (
              <div key={pillar} className="space-y-4">
                {/* Pillar Header with Description - Clickable */}
                <Card 
                  className={`${pillarStyle?.bg} ${pillarStyle?.border} border-l-4 cursor-pointer hover:shadow-md transition-all`}
                  onClick={() => togglePillar(pillar)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${pillarStyle?.badge?.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`h-6 w-6 ${pillarStyle?.text}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h2 className={`text-lg font-bold ${pillarStyle?.text}`}>{pillar}</h2>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${pillarStyle?.badge}`}>
                            {pillarDesc?.weight} Weight
                          </span>
                          <span className="text-xs text-slate-500">
                            {groupedMeasures[pillar]?.length} measure{groupedMeasures[pillar]?.length !== 1 ? 's' : ''}
                          </span>
                          <div className="ml-auto">
                            {isPillarExpanded ? (
                              <ChevronDown className="h-5 w-5 text-slate-500" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-slate-500" />
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-700 mb-2">{pillarDesc?.summary}</p>
                        {!isPillarExpanded && (
                          <p className="text-xs text-slate-500 italic">Click to expand and see {groupedMeasures[pillar]?.length} measures</p>
                        )}
                        {isPillarExpanded && (
                          <>
                            <p className="text-xs text-slate-600"><strong>Focus:</strong> {pillarDesc?.focus}</p>
                            <p className="text-xs text-slate-500 mt-2 italic">
                              <strong>Why it matters:</strong> {pillarDesc?.whyItMatters}
                            </p>
                            {pillarDesc?.businessImpact && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {pillarDesc.businessImpact.map((impact, idx) => (
                                  <span key={`impact-${impact.slice(0, 20)}-${idx}`} className="text-xs px-2 py-0.5 bg-white border border-slate-200 rounded-full text-slate-600">
                                    {impact}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Measures under this pillar - Only show when expanded */}
                {isPillarExpanded && (
                  <div className="space-y-3 ml-4">
                    {groupedMeasures[pillar].map(measure => (
                      <MeasureCard
                        key={measure.id}
                        measure={measure}
                        isExpanded={expandedMeasures[measure.id]}
                        onToggle={() => toggleMeasure(measure.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <RelatedReferences activeRef="lei" />

      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <p className="text-sm text-slate-600 text-center">
            Need help applying these measures to your analysis? 
            <a href="/portfolio" className="text-teal-600 hover:underline ml-1">
              Start a Portfolio Analysis
            </a>
            {' '}or{' '}
            <a href="/matrix" className="text-teal-600 hover:underline">
              Run a Full Impact Matrix
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
    </PageShell>
  );
};

export default LEIReferencePage;
